import React, { useMemo } from 'react';
import Select from 'react-select';
import { useSelectStyles } from '../../utils/selectStyles';
import { safeArray, safeNum, formatInt, TEMPLATE_FALLBACK_LABEL } from '../../utils/gpsFormUtils';
import TypeBadge from './explorer/TypeBadge';
import ExplorerNotice from './explorer/ExplorerNotice';
import ExplorerChart from './explorer/ExplorerChart';
import InfoTooltip from '../ui/InfoTooltip';

const RESPONDENTS_TOOLTIP =
  'Nombre de tickets distincts ayant au moins une réponse à ce formulaire sur la période. Un ticket compte une seule fois, quel que soit le nombre de formulaires qu\'il a remplis.';

// V1 product decision (2026-06-11): N-1 and % répondants toggles removed per lead Data
// feedback. Cohort comparison is unreliable (upstream purge); % switch is multichoice-only
// and confuses users. Backend + DeltaCell retained for possible V2.

const GPSFormExplorer = ({
  questions,
  breakdown,
  breakdownLoading,
  breakdownError,
  selectedTemplateId,
  selectedQuestionId,
  comparePrevious,
  denominator,
  onSelectTemplate,
  onSelectQuestion,
  project,
  currentUser,
}) => {
  const selectStyles = useSelectStyles();

  // The controls wrap each <Select> in a pill with `overflow-hidden`, which would
  // clip the dropdown menu to the 36px-tall control. Portal the menu to <body> so
  // it escapes the clip (z-index handled by selectStyles.menuPortal).
  const menuPortalTarget = typeof document !== 'undefined' ? document.body : undefined;

  // Build template options defensively from the catalog.
  const templates = safeArray(questions?.templates);

  const templateOptions = useMemo(
    () =>
      templates.map((t) => {
        const named = t?.templateName && String(t.templateName).trim() !== '';
        return {
          value: t?.templateId ?? t?.templateName ?? '',
          label: named ? t.templateName : TEMPLATE_FALLBACK_LABEL,
          isFallback: !named,
        };
      }),
    [templates],
  );

  // §13 — render the unnamed-form fallback label greyed/muted.
  const formatTemplateOption = (opt) =>
    opt?.isFallback ? (
      <span className="italic text-gray-400 dark:text-gray-500">{opt.label}</span>
    ) : (
      opt?.label
    );

  const activeTemplate = useMemo(() => {
    if (templates.length === 0) return null;
    const byId = templates.find(
      (t) => (t?.templateId ?? t?.templateName) === selectedTemplateId,
    );
    return byId || templates[0];
  }, [templates, selectedTemplateId]);

  const questionOptions = useMemo(
    () =>
      safeArray(activeTemplate?.questions).map((q) => ({
        value: q?.questionId,
        label: q?.label || q?.questionId || 'Question',
        answerType: q?.answerType,
      })),
    [activeTemplate],
  );

  // Real template id (NOT the name fallback) — question_id is unique only within a
  // template, so the breakdown/answers fetch must scope on it. Null when the form has
  // no template id; the backend then falls back to question_id-only (unnamed-form edge).
  const activeTemplateId = activeTemplate?.templateId ?? null;

  const selectedQuestionOption =
    questionOptions.find((q) => q.value === selectedQuestionId) || null;
  // Prefer the breakdown's resolved type, fall back to the catalog option's type.
  const badgeType = breakdown?.answerType || selectedQuestionOption?.answerType || 'unknown';

  const hasCatalog = templates.length > 0;

  const respondents = safeNum(activeTemplate?.respondents);

  return (
    <div className="text-left bg-white dark:bg-[#1e1e1e] p-6 rounded-xl border border-gray-200 dark:border-gray-900 shadow-sm">
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Explorateur</h3>

      {!hasCatalog ? (
        <ExplorerNotice>
          Aucune question de formulaire disponible pour ce projet sur la période et les filtres
          sélectionnés.
        </ExplorerNotice>
      ) : (
        <>
          {/* Controls row — inline labelled pills */}
          <div className="flex flex-wrap items-center gap-2 mb-5">
            {/* Formulaire pill */}
            <div className="inline-flex items-center gap-0 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#2a2a2a] overflow-hidden h-9 min-w-[180px] flex-1">
              <span className="px-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap border-r border-gray-200 dark:border-gray-700 h-full flex items-center">
                Formulaire&nbsp;:
              </span>
              <div className="flex-1 min-w-0">
                <Select
                  styles={{
                    ...selectStyles,
                    control: (base) => ({
                      ...base,
                      border: 'none',
                      boxShadow: 'none',
                      minHeight: '36px',
                      height: '36px',
                      backgroundColor: 'transparent',
                    }),
                    valueContainer: (base) => ({ ...base, padding: '0 8px' }),
                    indicatorSeparator: () => ({ display: 'none' }),
                  }}
                  options={templateOptions}
                  value={
                    templateOptions.find(
                      (o) =>
                        o.value ===
                        (activeTemplate?.templateId ?? activeTemplate?.templateName),
                    ) || null
                  }
                  onChange={(opt) => onSelectTemplate?.(opt?.value ?? null)}
                  formatOptionLabel={formatTemplateOption}
                  isSearchable={false}
                  placeholder="Formulaire"
                  aria-label="Formulaire"
                  menuPortalTarget={menuPortalTarget}
                  menuPosition="fixed"
                />
              </div>
            </div>

            {/* Question pill */}
            <div className="inline-flex items-center gap-0 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#2a2a2a] overflow-hidden h-9 min-w-[200px] flex-1">
              <span className="px-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap border-r border-gray-200 dark:border-gray-700 h-full flex items-center gap-1.5">
                Question&nbsp;:
                {selectedQuestionOption && <TypeBadge answerType={badgeType} />}
              </span>
              <div className="flex-1 min-w-0">
                <Select
                  styles={{
                    ...selectStyles,
                    control: (base) => ({
                      ...base,
                      border: 'none',
                      boxShadow: 'none',
                      minHeight: '36px',
                      height: '36px',
                      backgroundColor: 'transparent',
                    }),
                    valueContainer: (base) => ({ ...base, padding: '0 8px' }),
                    indicatorSeparator: () => ({ display: 'none' }),
                  }}
                  options={questionOptions}
                  value={selectedQuestionOption}
                  onChange={(opt) => onSelectQuestion?.(opt?.value ?? null, activeTemplateId)}
                  placeholder="Question"
                  aria-label="Question"
                  menuPortalTarget={menuPortalTarget}
                  menuPosition="fixed"
                />
              </div>
            </div>

          </div>

          {/* Respondents counter — tickets that filled the active form */}
          <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
            {respondents > 0 ? (
              <>
                {`${formatInt(respondents)} tickets répondants sur la période`}
                <InfoTooltip text={RESPONDENTS_TOOLTIP} className="ml-1" />
              </>
            ) : (
              "Aucun ticket n'a répondu à ce formulaire sur la période et les filtres sélectionnés."
            )}
          </p>

          {/* Chart slot */}
          <ExplorerChart
            breakdown={breakdown}
            loading={breakdownLoading}
            error={breakdownError}
            comparePrevious={comparePrevious}
            denominator={denominator}
            project={project}
            currentUser={currentUser}
          />
        </>
      )}
    </div>
  );
};

export default GPSFormExplorer;
